import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Users,
  DollarSign,
  TrendingUp,
  UserX,
  FileText,
  Phone,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Pause
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const CreditorsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    setTimeout(() => {
      const mockCreditors = [
        {
          id: 'CRED-001',
          name: 'شركة المحاماة المتقدمة',
          type: 'company',
          phone: '0501234567',
          email: 'info@advanced-law.com',
          balanceDue: 45000,
          invoiceCount: 8,
          lastTransaction: '2024-11-25',
          status: 'active',
          category: 'خدمات قانونية'
        },
        {
          id: 'CRED-002',
          name: 'أحمد المحامي',
          type: 'individual',
          phone: '0559876543',
          email: 'ahmed@lawyer.com',
          balanceDue: 12000,
          invoiceCount: 3,
          lastTransaction: '2024-12-01',
          status: 'active',
          category: 'استشارات'
        },
        {
          id: 'CRED-003',
          name: 'مكتب التوثيق القانوني',
          type: 'company',
          phone: '0112345678',
          email: 'contact@notary.sa',
          balanceDue: 28000,
          invoiceCount: 5,
          lastTransaction: '2024-11-15',
          status: 'suspended',
          category: 'توثيق'
        },
        {
          id: 'CRED-004',
          name: 'فاطمة الاستشارية',
          type: 'individual',
          phone: '0541112233',
          email: 'fatima@consult.com',
          balanceDue: 8500,
          invoiceCount: 2,
          lastTransaction: '2024-12-05',
          status: 'active',
          category: 'استشارات'
        },
        {
          id: 'CRED-005',
          name: 'شركة الترجمة القانونية',
          type: 'company',
          phone: '0126667788',
          email: 'legal@translate.sa',
          balanceDue: 0,
          invoiceCount: 4,
          lastTransaction: '2024-10-20',
          status: 'inactive',
          category: 'ترجمة'
        },
        {
          id: 'CRED-006',
          name: 'محمد الخبير القانوني',
          type: 'individual',
          phone: '0503334455',
          email: 'mohamed@expert.com',
          balanceDue: 15000,
          invoiceCount: 6,
          lastTransaction: '2024-11-30',
          status: 'frozen',
          category: 'خبرة قانونية'
        }
      ];
      setCreditors(mockCreditors);
      setLoading(false);
    }, 500);
  }, []);

  // Calculate stats
  const stats = {
    totalCreditors: creditors.filter(c => c.status !== 'inactive').length,
    totalDue: creditors.reduce((sum, c) => sum + c.balanceDue, 0),
    topCreditor: creditors.length > 0
      ? creditors.reduce((max, c) => (c.balanceDue > max.balanceDue ? c : max), creditors[0])
      : null,
    inactiveCreditors: creditors.filter(c => c.status === 'inactive').length
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        label: 'نشط',
        icon: CheckCircle,
        className: 'bg-green-50 text-green-700 border-green-200'
      },
      suspended: {
        label: 'موقوف',
        icon: Pause,
        className: 'bg-orange-50 text-orange-700 border-orange-200'
      },
      frozen: {
        label: 'مجمّد',
        icon: XCircle,
        className: 'bg-red-50 text-red-700 border-red-200'
      },
      inactive: {
        label: 'غير نشط',
        icon: UserX,
        className: 'bg-gray-50 text-gray-700 border-gray-200'
      }
    };

    const config = statusConfig[status] || statusConfig.inactive;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Filter creditors
  const filteredCreditors = creditors.filter(creditor => {
    const matchesSearch =
      creditor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      creditor.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      creditor.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || creditor.status === filterStatus;
    const matchesType = filterType === 'all' || creditor.type === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Handle delete
  const handleDelete = (creditorId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الدائن؟')) {
      alert(`تم حذف الدائن ${creditorId}`);
    }
  };

  // Handle toggle status
  const handleToggleStatus = (creditorId, currentStatus) => {
    const action = currentStatus === 'active' ? 'إيقاف' : 'تفعيل';
    if (window.confirm(`هل تريد ${action} هذا الدائن؟`)) {
      alert(`تم ${action} الدائن ${creditorId}`);
    }
  };

  // Handle export
  const handleExport = () => {
    alert('سيتم تصدير كشف الحساب قريباً');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل الدائنين...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <Users className="w-8 h-8" />
                <span>إدارة الدائنين</span>
              </h1>
              <p className="text-gray-600">إدارة وتتبع جميع الدائنين والموردين</p>
            </div>
            {hasPermission('finance.creditors.create') && (
              <button
                onClick={() => navigate('/admin/finance/creditors/create')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all"
              >
                <Plus className="w-5 h-5" />
                <span>إضافة دائن جديد</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">عدد الدائنين</p>
                <p className="text-3xl font-black text-[#1E3A8A]">{stats.totalCreditors}</p>
              </div>
              <Users className="w-12 h-12 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي المبالغ المستحقة</p>
                <p className="text-2xl font-black text-red-600">{stats.totalDue.toLocaleString()} ر.س</p>
              </div>
              <DollarSign className="w-12 h-12 text-red-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-[#C8A870] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">أعلى دائن</p>
                <p className="text-lg font-black text-[#C8A870]">
                  {stats.topCreditor ? stats.topCreditor.name.substring(0, 15) + '...' : 'لا يوجد'}
                </p>
                {stats.topCreditor && (
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.topCreditor.balanceDue.toLocaleString()} ر.س
                  </p>
                )}
              </div>
              <TrendingUp className="w-12 h-12 text-[#C8A870] opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الدائنون غير النشطين</p>
                <p className="text-3xl font-black text-gray-600">{stats.inactiveCreditors}</p>
              </div>
              <UserX className="w-12 h-12 text-gray-400 opacity-20" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث باسم الدائن، الرقم، أو البريد..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الحالات</option>
                <option value="active">نشط</option>
                <option value="suspended">موقوف</option>
                <option value="frozen">مجمّد</option>
                <option value="inactive">غير نشط</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="relative">
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الأنواع</option>
                <option value="individual">فرد</option>
                <option value="company">شركة</option>
              </select>
            </div>
          </div>
        </div>

        {/* Creditors Table */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">اسم الدائن</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">النوع</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الرصيد المستحق</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">عدد الفواتير</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">آخر تعامل</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCreditors.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>لا يوجد دائنون</p>
                    </td>
                  </tr>
                ) : (
                  filteredCreditors.map((creditor) => (
                    <tr key={creditor.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-bold text-[#1E3A8A]">{creditor.name}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <Mail className="w-3 h-3" />
                            {creditor.email}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">
                          {creditor.type === 'individual' ? 'فرد' : 'شركة'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-bold text-lg ${creditor.balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {creditor.balanceDue.toLocaleString()} ر.س
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-bold">
                          <FileText className="w-3 h-3" />
                          {creditor.invoiceCount}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700 flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {creditor.lastTransaction}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(creditor.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {/* View Details */}
                          <button
                            onClick={() => navigate(`/admin/finance/creditors/${creditor.id}`)}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                            title="عرض التفاصيل"
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </button>

                          {/* Edit */}
                          {hasPermission('finance.creditors.edit') && (
                            <button
                              onClick={() => navigate(`/admin/finance/creditors/${creditor.id}/edit`)}
                              className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="تعديل"
                            >
                              <Edit className="w-4 h-4 text-yellow-600" />
                            </button>
                          )}

                          {/* Export Statement */}
                          {hasPermission('finance.export') && (
                            <button
                              onClick={handleExport}
                              className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                              title="تصدير كشف حساب"
                            >
                              <Download className="w-4 h-4 text-green-600" />
                            </button>
                          )}

                          {/* Delete */}
                          {hasPermission('finance.creditors.delete') && (
                            <button
                              onClick={() => handleDelete(creditor.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Footer */}
        {filteredCreditors.length > 0 && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">عرض {filteredCreditors.length} من أصل {creditors.length} دائن</span>
              <span className="font-bold text-[#1E3A8A]">
                إجمالي المبالغ المعروضة: {filteredCreditors.reduce((sum, c) => sum + c.balanceDue, 0).toLocaleString()} ر.س
              </span>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CreditorsPage;